import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { TILE_SIZE } from '@shared/constants';
import { ClientMessageType, type RequestTileInfoMessage } from '@shared/types/messages';

/**
 * Interacción del mundo por puntero: el click izquierdo (handleWorldClick) resuelve qué hacer según
 * el modo (castear hechizo en el tile, atacar/targetear entidad, mover), con el guard de paneles
 * abiertos (isPointerOverAnyOpenPanel), más las acciones de castear en tile (executeSpellAtTile) y
 * levantar item del tile del player (requestPickupItemAtPlayerTile). Extraído de GameScene
 * (god-object). El estado (input, target, spellCastMode…) vive en GameScene y se accede vía
 * `this.scene.*`. Ver docs/TARGETING.md, docs/SPELLS.md.
 */
export class InteractionController {
  constructor(private scene: GameScene) {}

  public executeSpellAtTile(tileX: number, tileY: number): void {
    if (!this.scene.spellTargeting || !this.scene.colyseusClient) return;

    const { spellId } = this.scene.spellTargeting;
    this.scene.lastSpellCastTime = Date.now();

    // Cooldown starts only when server confirms success (via onSpellCastResult)
    this.scene.colyseusClient.send({
      type: 'cast_spell' as any,
      spellId,
      targetX: tileX,
      targetY: tileY,
    } as any);

    this.scene.spellCast.exitSpellTargeting();
  }

  public requestPickupItemAtPlayerTile(): void {
    if (!this.scene.localSessionId) return;

    const render = this.scene.playerObjects.get(this.scene.localSessionId);

    if (!render) return;

    // Tile actual (usando posición visual para que coincida con lo que ve el jugador)

    const tileX = Math.floor(render.currentWorldX / TILE_SIZE);

    const tileY = Math.floor(render.currentWorldY / TILE_SIZE);

    // Buscar primer ground item en ese tile

    let targetId: string | null = null;
    let targetItemId: string | null = null;

    this.scene.groundItemObjects.forEach((obj, id) => {
      if (targetId) return;

      const gx = Math.floor(obj.sprite.x / TILE_SIZE);

      const gy = Math.floor(obj.sprite.y / TILE_SIZE);

      if (gx === tileX && gy === tileY) {
        targetId = id;
        targetItemId = obj.itemId;
      }
    });

    if (targetId) {
      this.scene.groundItems.requestPickupGroundItem(targetId);
      // Sonido de levantar (por tipo de armadura del ítem; default para otros).
      if (targetItemId) this.scene.audioWorld.playPickupSound(targetItemId);
    }
  }

  public isPointerOverAnyOpenPanel(pointer: Phaser.Input.Pointer): boolean {
    if (this.scene.isMenuOpen || this.scene.invokeIsOpen || this.scene.bodyGalleryPanel?.isOpenPanel() || this.scene.weaponGalleryPanel?.isOpenPanel() || this.scene.charFrameDebugPanel?.isOpenPanel()) return true;
    return Boolean(
      this.scene.inventoryPanel?.isPointerOver(pointer) ||
        this.scene.characterInfoPanel?.isPointerOver(pointer) ||
        this.scene.targets.targetPanel?.isPointerOver(pointer) ||
        this.scene.npcInfoPanel?.isPointerOver(pointer) ||
        this.scene.merchantPanel?.isPointerOver(pointer) ||
        this.scene.exchangePanel?.isPointerOver(pointer) ||
        this.scene.marketplacePanel?.isPointerOver(pointer) ||
        this.scene.spellVendorPanel?.isPointerOver(pointer) ||
        this.scene.itemPanel?.isPointerOver(pointer) ||
        this.scene.animPanel?.isPointerOver(pointer) ||
        this.scene.particlePanel?.isPointerOver(pointer) ||
        this.scene.iconViewerPanel?.isPointerOver(pointer)
    );
  }

  public handleWorldClick(pointer: Phaser.Input.Pointer): void {
    // Si hay menú, chat activo o ventanas abiertas (con el pointer encima),
    // ignoramos para no interferir con la UI.
    if (this.scene.chatIsFocused || this.isPointerOverAnyOpenPanel(pointer)) return;

    // AO-like: Convertir coordenadas de pantalla a coordenadas de mundo
    const screenX = pointer.x;
    const screenY = pointer.y;
    const worldX = this.scene.cameraCtl.screenToWorldX(screenX);
    const worldY = this.scene.cameraCtl.screenToWorldY(screenY);

    // Convertir a coordenadas de tile
    const tileX = Math.floor(worldX / TILE_SIZE);
    const tileY = Math.floor(worldY / TILE_SIZE);

    // Admin: Shift + click izquierdo → teleport al tile bajo el cursor.
    // Gateado por isLocalAdmin (UX); el server revalida player.isAdmin.
    if (pointer.event?.shiftKey && this.scene.isLocalAdmin()) {
      this.scene.colyseusClient?.send({
        type: ClientMessageType.ADMIN_TELEPORT,
        x: tileX,
        y: tileY,
      });
      return; // no target-lock ni REQUEST_TILE_INFO
    }

    // If in spell targeting mode, execute spell at this tile
    // Ignore clicks within 200ms of entering targeting (prevents slot click from firing immediately)
    if (this.scene.spellTargeting) {
      if (Date.now() - this.scene.spellTargeting.startedAt < 200) return;
      this.executeSpellAtTile(tileX, tileY);
      return;
    }

    // Fuera de los límites del mapa lógico
    if (
      tileX < 0 ||
      tileY < 0 ||
      tileX >= GameScene.WORLD_TILES_X ||
      tileY >= GameScene.WORLD_TILES_Y
    ) {
      return;
    }

    // === TARGET LOCK (paradigma WoW) ===
    // Click sobre entity (NPC u otro jugador): set target.
    // Click sobre tile vacío: clear target.
    // Detección por caja visible del sprite (no por tile lógico) — clickear el
    // cuerpo/cabeza del NPC ahora selecciona, no sólo los pies.
    const hit = this.scene.targets.findEntityAtWorld(worldX, worldY);
    if (hit) {
      // Estando muerto sólo se puede seleccionar un NPC que revive (sacerdote).
      // Cualquier otro hit (enemigo, otro player, vos mismo) no setea target.
      // El server revalida igual (handleSelectTargetMessage).
      if (this.scene.isLocalPlayerDead() && !(hit.type === 'npc' && this.scene.isPriestNpc(hit.id))) {
        this.scene.targets.clearTarget();
      } else {
        this.scene.targets.setTarget(hit.type, hit.id);
      }
    } else {
      this.scene.targets.clearTarget();
    }

    // Enviar solicitud al servidor para obtener información del tile en tiempo real
    // (sigue siendo útil para debug/consola y para listar items en el piso).
    // Estando muerto no se consulta: un muerto no debe ver en consola lo que clickea.
    if (!this.scene.isLocalPlayerDead() && this.scene.colyseusClient && this.scene.colyseusClient.isConnected()) {
      const requestMessage: RequestTileInfoMessage = {
        type: ClientMessageType.REQUEST_TILE_INFO,
        x: tileX,
        y: tileY,
      };
      this.scene.colyseusClient.send(requestMessage);
    }
  }

}
